import * as Hogan from 'hogan.js';
import {Loader, LoaderResource, utils} from 'pixi.js';
import {
    consoleLogger,
    CoreDI,
    Device,
    DeviceType,
    IEventManagerWithAsync, InitialResources, inject, injectable,
    IService, Platform,
    ResolutionManager, ResourceBuilderHandler,
    ResourceLoader, Resources, Settings,
    SystemComponentType, Text, ResourceLoadPriority, ResourcesPack, UpdateManager
} from '@gongxigames/framework/core';
import {GlobalEventsMap} from '@gongxigames/framework/slot/events';
import {EnvironmentProperty, GameConfig} from '@gongxigames/framework/slot/types';
import {builderX} from '@gongxigames/framework/core/fragments/BuilderX';
import {TemplateUtils} from '@gongxigames/framework/slot/services/TemplateUtils';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {CONNECTION_ERROR} from '@gongxigames/framework/slot/configs/messages';
import {ImageFormat} from '@gongxigames/framework/resourcesBuilder/types';
import {strictUiEmitter} from '@gongxigames/framework/slotV2/globals/strictUiEmitter';
import {strictCoreEmitter} from '@gongxigames/framework/core/globals';

export type ResourceLoaderParameters = {
    cdn: string;
    root: string;
    deviceType?: DeviceType;
    resolution?: string;
    isSoundSupported: boolean;
    isSoundsLoadingDelayed: boolean;
    imageFormat: ImageFormat;
    resourcePaths: {
        binary?: string;
        staticFolder: string;
    };
};

type LoaderResourcesMap = Record<string, LoaderResource>;

@injectable()
export class ResourceManager implements IService {
    public static NAME: symbol = DI.ResourceManager;
    public type: SystemComponentType = 'service';

    @inject(CoreDI.ResolutionManager)
    protected _resolutionManager!: ResolutionManager;

    @inject(CoreDI.Device)
    protected _device!: Device;

    @inject(CoreDI.UpdateManager)
    protected _updateManager!: UpdateManager;

    @inject(CoreDI.IEventManagerWithAsync)
    protected _eventManager!: IEventManagerWithAsync<GlobalEventsMap>;

    @inject(CoreDI.ResourceLoader)
    protected _loader!: ResourceLoader;

    @inject(CoreDI.Settings)
    protected _settings!: Settings;

    @inject(CoreDI.EnvironmentProperty)
    protected _environmentProperty!: EnvironmentProperty;

    @inject(DI.TemplateUtils)
    protected _templateUtils!: TemplateUtils;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    protected _resourceHandler: ResourceBuilderHandler = new ResourceBuilderHandler();

    protected _pixiLoader!: Loader;
    protected _binaryResourcesUrl!: string;
    protected _staticUrl!: string;

    protected _isResourcesLoaded = false;

    protected _parameters!: ResourceLoaderParameters;
    private _styles: HTMLStyleElement[] = [];

    private packsToLoadByPriority: Record<ResourceLoadPriority, string[]> = {
        before: ['before_priority'],
        default: ['default_priority'],
        after: ['after_priority']
    };

    public async init(): Promise<void> {
        const {soundsMuted, soundEnabled, cdn, root, platform} = this._settings.getAllProperties();
        const isMobilePlatform = platform === DeviceType.MOBILE;
        const isSoundsLoadingDelayed = !soundEnabled || (soundEnabled && soundsMuted && isMobilePlatform);
        const {isSoundSupported} = this._device;
        const {resourcePaths} = this._environmentProperty.buildProperty.gameProperty;

        this._parameters = {
            imageFormat: this.getImageFormat(),
            cdn,
            root,
            deviceType: platform,
            resourcePaths,
            isSoundsLoadingDelayed,
            isSoundSupported
        };

        this._pixiLoader = Loader.shared;
        this._pixiLoader.onError.add(this.onLoadError.bind(this));

        this.initURLs();
        this.resourceHandlerInit();
        this.setupEvents();
    }

    public destroy(): void {
        this.clearCache();

        this._styles.forEach(style => style.remove());
        this._styles = [];
    }

    public clearCache(): void {
        const {slot_fonts, game_fonts} = this._pixiLoader.resources;
        const fonts: {fileName: string;}[] = game_fonts && game_fonts.data ? [...game_fonts.data, ...slot_fonts.data] : slot_fonts.data;

        if (fonts) {
            fonts.forEach(font => {
                document.getElementById(font.fileName)?.remove();
            });
        }

        this._pixiLoader.reset();
        this._pixiLoader.onError.detachAll();
        this._pixiLoader.onStart.detachAll();
        this._pixiLoader.onComplete.detachAll();
        this._pixiLoader.onLoad.detachAll();
        this._pixiLoader.onProgress.detachAll();
        utils.clearTextureCache();
    }

    public addResourcesPackToLoad(pack: ResourcesPack): ResourceManager {
        Object.keys(pack as any).forEach(key => {
            const priority: ResourceLoadPriority = key as ResourceLoadPriority;
            const name = pack[priority];

            if (priority && name) {
                this.packsToLoadByPriority[priority].push(name);
            }
        });

        return this;
    }

    public async loadInitialResources(): Promise<InitialResources> {
        const lang = this._settings.get('lang');

        // resources for notification message
        this._pixiLoader.add('notificationMessage', `${this._staticUrl}templates/notificationMessage.hjs`);
        // resources for error popups
        this._pixiLoader.add('popup', `${this._staticUrl}templates/popup.hjs`);
        // resources for error popups
        this._pixiLoader.add('promotionPopup', `${this._staticUrl}templates/promotionPopup.hjs`);
        // languages
        this._pixiLoader.add('slotLang', `${this._staticUrl}slot_lang/${lang}.json`);

        return new Promise<InitialResources>((res) => this._pixiLoader.load((loader: Loader) => res(loader.resources as InitialResources)));
    }

    public loadGameTexts(): Promise<LoaderResourcesMap> {
        const lang = this._settings.get('lang');

        this._pixiLoader.add('lang', `${this._staticUrl}lang/${lang}.json`);

        return new Promise((resolve, reject) => {
            this._pixiLoader.load(response => resolve(response.resources));
            this._pixiLoader.onError.add(reject);
        });
    }

    public loadStaticImages(images: string[], onLoaded: Function = (_resources: LoaderResourcesMap) => {}): Loader {
        const loader = new Loader();

        loader.baseUrl = `${this._staticUrl}images/`;

        const onLoadedCallback = ({resources}: {resources: LoaderResourcesMap;}): void => {
            onLoaded(resources);
        };

        loader.onError.add(this.onLoadError.bind(this));

        images.forEach(img => loader.add(img, img));

        loader.load(onLoadedCallback);

        return loader;
    }

    public loadTemplate(name: string, onLoaded: Function = () => {}): Promise<string> {
        const url = `${this._staticUrl}templates/${name}.hjs`;

        return this.loadTextFile({name, url, onLoaded});
    }

    public loadHTMLTemplate(name: string, onLoaded: Function = () => {}): Promise<string> {
        const url = `${this._staticUrl}html/${name}.hjs`;

        return this.loadTextFile({name, url, onLoaded});
    }

    public loadTextFile(parameters: {name: string; url: string; onLoaded?: Function;}): Promise<string> {
        const {
            name,
            url,
            onLoaded = () => {}
        } = parameters;

        return new Promise( (resolve, reject) => {
            const loader = new Loader();
            const onLoadedCallback = ({resources}: {resources: LoaderResourcesMap;}): void => {
                if (resources.error) {
                    reject();
                }

                const text = resources[name].data;

                onLoaded(text);
                resolve(text);
            };

            loader.onError.add(reject);
            loader.onError.add(this.onLoadError.bind(this));
            loader.add(name, url);
            loader.load(onLoadedCallback);
        });
    }

    public get isResourcesLoaded(): boolean {
        return this._isResourcesLoaded;
    }

    public get isSoundsLoadingDelayed(): boolean {
        return this._parameters.isSoundsLoadingDelayed;
    }

    public loadMetaDataForResources(): Promise<void> {
        const MetaDataResources = [{
            // TODO - fonts json move to studio in resources.json
            name: 'slot_fonts',
            url: `${this._staticUrl}fonts/slot_fonts.json`,
            stopLoadOnError: true
        }, {
            name: 'game_fonts',
            url: `${this._staticUrl}fonts/game_fonts.json`
        }, {
            name: 'autoplayPopup',
            url: `${this._staticUrl}templates/promotionMeter.hjs`
        }, {
            name: 'promotionMeter',
            url: `${this._staticUrl}templates/promotionMeter.hjs`
        }, {
            name: 'relaxPromotionMeter',
            url: `${this._staticUrl}templates/relaxPromotionMeter.hjs`
        }];

        // load resources meta data
        MetaDataResources.forEach(({name, url}) => {
            this._pixiLoader.add(name, url);
        });

        return new Promise( res => {
            this._pixiLoader.load( _ => {
                const stopLoad = MetaDataResources.find(item => {
                    const {name} = item;

                    if (item.stopLoadOnError && this._pixiLoader.resources[name].error) {
                        return true;
                    }
                });

                if (stopLoad) {
                    return;
                }

                builderX.updateResources();

                res();

                this._eventManager.emit('General.ResourcesLoading.LOADED_META_DATA');
            });
        });
    }

    public async loadResourcesWithBeforePriority(): Promise<void> {
        const {slot_fonts, game_fonts} = this._pixiLoader.resources;

        // load fonts
        const fonts = game_fonts && game_fonts.data ? [...game_fonts.data, ...slot_fonts.data] : slot_fonts.data;

        fonts.forEach( (fontData: any) => {
            this._loader.addFont({
                dir: `${this._staticUrl}fonts`,
                ...fontData
            }, 0);

            Text.addAscentPercent(fontData.family, fontData.ascentPercent);
        });

        await new Promise( res => {
            this._loader.load(res, undefined, this.onLoadError.bind(this));
        });

        this.resourceHandlerUpdateConfig();

        await this._resourceHandler.load({
            names: this.packsToLoadByPriority.before,
            onLoaded: (res) => this.onResourceHandlerPackageLoaded(res),
            toLoad: this.isToLoadSounds ? 'all' : 'graphics'
        });

        this._eventManager.emit('General.ResourcesLoading.LOADED_RESOURCES', 'before');
    }

    public async loadDeviceStyle(): Promise<void> {
        const platform = this._parameters.deviceType;
        const styleName = this._gameConfig.gameProvider ? 'main' : platform;
        const styles = await new Promise<string>(res => {
            this._pixiLoader.add('deviceStyle', `${this._staticUrl}styles/${styleName}.css`, (response: LoaderResource) => {
                if (response.error) {
                    return;
                }

                res(response.data);
            });
        });

        this.compileCSSTemplateAndAppendCSSToDocument(styles);
    }

    private compileCSSTemplateAndAppendCSSToDocument(cssTemplate: string): void {
        const head = document.getElementsByTagName('head')[0];
        const style = document.createElement('style') as any;
        const template = Hogan.compile(cssTemplate);
        const language = this._settings.get('lang');
        const images = this._templateUtils.createImageMap(cssTemplate, language);
        const css = template.render({
            images
        });

        if (style.styleSheet) {
            style.styleSheet.cssText = css;
        } else {
            style.appendChild(document.createTextNode(css));
        }

        head.appendChild(style);

        this._styles.push(style);
    }

    public async loadResourcesWithDefaultPriority(): Promise<void> {
        this._eventManager.emit('General.ResourcesLoading.START_LOAD_RESOURCES', 'default');

        await this._resourceHandler.load({
            names: this.packsToLoadByPriority.default,
            onLoaded: (res) => this.onResourceHandlerPackageLoaded(res),
            onProgress: (progress) => this._eventManager.emit('General.ResourcesLoading.PROGRESS', progress),
            toLoad: this.isToLoadSounds ? 'all' : 'graphics'
        });

        this.isToLoadSounds && this._eventManager.emit('General.ResourcesLoading.LOADED_ALL_SOUNDS');
        this._eventManager.emit('General.ResourcesLoading.LOADED_RESOURCES', 'default');
    }

    public async loadResourcesWithAfterPriority(): Promise<void> {
        await this._resourceHandler.load({
            names: this.packsToLoadByPriority.after,
            onLoaded: (res) => this.onResourceHandlerPackageLoaded(res),
            toLoad: this.isToLoadSounds ? 'all' : 'graphics'
        });

        consoleLogger.log('ALL RESOURES LOADED');

        this._isResourcesLoaded = true;
        this._eventManager.emit('General.ResourcesLoading.LOADED_RESOURCES', 'after');
        this._eventManager.emit('General.ResourcesLoading.LOADED_ALL_RESOURCES');
        strictCoreEmitter.emit('General.ResourcesLoading.LOADED_ALL_RESOURCES');
    }

    private get isToLoadSounds(): boolean {
        const {isSoundSupported, isSoundsLoadingDelayed} = this._parameters;

        return isSoundSupported && !isSoundsLoadingDelayed;
    }

    protected onLoadError(error: any, loader: Loader, resource: LoaderResource): void {
        consoleLogger.error(error);

        if (resource.name === 'game_fonts') {
            consoleLogger.error('No game_fonts.json! It is necessary to add game_fonts.json to files/fonts/');

            return;
        }

        this._eventManager.emit('General.ResourcesLoading.ERROR');
        this._eventManager.emit('General.Message.SHOW', CONNECTION_ERROR);
    }

    private async resourceHandlerInit(): Promise<void> {
        const platform = this._settings.get('platform') as Platform;
        const locale = this._settings.get('lang');
        const override = this._settings.get('override');
        const audioQuality = this._device.getSoundQuality();
        const audioFormats: string[] = [];
        const {imageFormat} = this._parameters;
        const useWebPWASMDecoder = !this._device.webp && imageFormat === ImageFormat.WEBP;

        if (this._device.canPlayAudio('ogg')) {
            audioFormats.push('ogg');
        }

        if (this._device.canPlayAudio('m4a')) {
            audioFormats.push('m4a');
        }

        this._resourceHandler.init({
            platform,
            urlRoot: this._binaryResourcesUrl,
            resolution: this.getResolution(),
            audioFormats,
            audioQuality,
            locale,
            override,
            useWebPWASMDecoder,
            imageFormat
        });
    }

    private onResourceHandlerPackageLoaded(resources: Resources): void {
        const loader = this._loader;

        Object.entries(resources.textures).forEach(([key, val]) => loader.setTexture(key, val));
        Object.entries(resources.spines).forEach(([key, val]) => loader.setSpine(key, val));
        Object.entries(resources.sounds).forEach(([key, val]) => loader.setSound(key, val));
    }

    private setupEvents(): void {
        const {isSoundSupported, isSoundsLoadingDelayed} = this._parameters;

        if (isSoundSupported && isSoundsLoadingDelayed) {
            strictUiEmitter.on('UI.sounds.switchedTo', this.handleSoundLoading.bind(this), {owner: this});
        }
    }

    private getResolution(): string {
        const resolutionData = this._resolutionManager.resolution;

        return this._parameters.resolution || resolutionData.width + '_' + resolutionData.height;
    }

    private initURLs(): void {
        const {binary, staticFolder} = this._parameters.resourcePaths;
        const {cdn, root} = this._parameters;

        this._binaryResourcesUrl = `${cdn}/${binary}`;
        this._staticUrl = `${root}/${staticFolder}`;
    }

    private handleSoundLoading(enabled: boolean): void {
        if (!enabled) {
            return;
        }

        if (this.isResourcesLoaded) {
            this.loadAllSounds();
        } else {
            this._eventManager.on('General.ResourcesLoading.LOADED_ALL_RESOURCES', this.loadAllSounds.bind(this), this);
        }
    }

    private loadAllSounds(): void {
        strictUiEmitter.offByOwner('UI.sounds.switchedTo', this);

        this._resourceHandler.load({
            onLoaded: res => {
                this.onResourceHandlerPackageLoaded(res);
                this._eventManager.emit('General.ResourcesLoading.LOADED_ALL_SOUNDS');
            },
            toLoad: 'audio'
        });
    }

    /**
     * update locale for regulations (for example 're-en')
     */
    private resourceHandlerUpdateConfig(): void {
        this._resourceHandler.updateConfig({
            locale: this._settings.get('lang')
        });
    }

    private getImageFormat(): ImageFormat {
        const getFormat = (isAvif = false) => isAvif ? ImageFormat.AVIF: ImageFormat.WEBP;
        const forceImageFormat = this._settings.get('imageFormat');

        if (forceImageFormat) {
            return getFormat(forceImageFormat === ImageFormat.AVIF);
        }

        return getFormat(this._device.avif);
    }
}
